import { useEffect, useMemo, useRef, useState } from 'react';
import { SaveError } from './SaveError';
import { useDialogFocus } from '@shared/hooks/useDialogFocus';
import { useTranslation } from 'react-i18next';
import { supabase } from '@shared/supabase/client';
import { flushEmailQueue } from '@shared/email/flushQueue';
import { useSupabaseQuery, useInvalidateSupabase } from '@shared/supabase/hooks';
import { Avatar } from '@shared/ui/Avatar';
import { NamedIcon } from '@shared/ui/icons';
import { IgLink } from '@shared/ui/IgLink';
import CreatorCardModal from './CreatorCardModal';
import { useNavigate } from 'react-router-dom';
import { useCreator } from './useCreator';

interface SearchRow { provider_id: string; handle: string; full_name: string | null; followers: number | null; er: number | null; on_adamar: boolean; avatar: string | null }
interface SuggestRow { provider_id: string; handle: string; followers: number | null; er: number | null; shared_niches: number | null; why: string | null; avatar: string | null }
interface CollabRow {
  proposal_id: string; direction: 'in' | 'out'; status: string;
  other_provider_id: string; other_handle: string; other_full_name: string | null; other_avatar: string | null; other_followers: number | null;
  on_adamar: boolean; message: string | null; created_at: string;
  last_message: string | null; last_message_at: string | null; unread: number;
}
interface MsgRow { id: string; mine: boolean; body: string; created_at: string }

const fmtK = (n: number | null | undefined) =>
  n == null ? '—' : n >= 1_000_000 ? `${(n / 1e6).toFixed(1)}M` : n >= 1000 ? `${Math.round(n / 1000)}K` : String(n);

const card: React.CSSProperties = { background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 12, padding: '12px 14px' };
const primaryBtn: React.CSSProperties = { background: 'rgba(74,158,255,0.16)', border: '1px solid rgba(74,158,255,0.45)', color: '#bcd0ff', borderRadius: 8, padding: '6px 12px', fontSize: 12.5, cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap' };
const ghostBtn: React.CSSProperties = { background: 'transparent', border: '1px solid rgba(255,255,255,0.18)', color: '#cdd', borderRadius: 8, padding: '6px 12px', fontSize: 12.5, cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap' };
const inputStyle: React.CSSProperties = { width: '100%', boxSizing: 'border-box', padding: '10px 12px', borderRadius: 10, background: 'rgba(0,0,0,0.25)', color: '#eee', border: '1px solid rgba(74,158,255,0.25)', fontSize: 14, fontFamily: 'inherit' };

function useDebounced<T>(v: T, ms: number): T {
  const [d, setD] = useState(v);
  useEffect(() => { const id = setTimeout(() => setD(v), ms); return () => clearTimeout(id); }, [v, ms]);
  return d;
}

const errMsg = (tr: (k: string, d: string) => string, e: string) =>
  e.includes('already_active') ? tr('creator.collab.errActive', 'С этим блогером уже есть активное предложение или коллаб.')
  : e.includes('not_open') ? tr('creator.collab.errClosed', 'Этот блогер закрыл приём предложений о коллабах.')
  : e.includes('cannot_self') ? tr('creator.collab.errSelf', 'Нельзя предложить коллаб самому себе.')
  : e.includes('no_target') ? tr('creator.collab.errTarget', 'Блогер не найден.')
  : tr('creator.collab.actFailed', 'Не получилось. Проверьте интернет и попробуйте ещё раз.');

// ── Compose a proposal ───────────────────────────────────────────────────────
function ComposeModal({ target, onClose, onSent }: { target: { provider_id: string; handle: string }; onClose: () => void; onSent: () => void }) {
  const { t } = useTranslation('provider');
  const tr = (k: string, d: string) => t(k, { defaultValue: d });
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const send = async () => {
    setBusy(true); setErr(null);
    const { error } = await supabase.rpc('creator_propose_collab', { p_to_provider: target.provider_id, p_message: msg.trim() || null });
    setBusy(false);
    if (error) { setErr(errMsg(tr, error.message)); return; }
    void flushEmailQueue(); // fire-and-forget: send the "new collab proposal" email now
    onSent();
  };
  const panelRef = useDialogFocus(onClose);
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 70, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
      <div ref={panelRef} role="dialog" aria-modal="true" aria-labelledby="collab-compose-title" onClick={(e) => e.stopPropagation()} style={{ width: '100%', maxWidth: 460, outline: 'none', background: '#12151c', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '16px 16px 0 0', padding: '18px 18px calc(20px + env(safe-area-inset-bottom))', display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div id="collab-compose-title" style={{ fontSize: 16, fontWeight: 600, color: '#eaf1fb' }}>{tr('creator.collab.proposeTo', 'Предложить коллаб')} @{target.handle}</div>
        <textarea value={msg} onChange={(e) => setMsg(e.target.value)} rows={3} aria-label={tr('creator.collab.msgLabel', 'Сообщение блогеру')} placeholder={tr('creator.collab.msgPh', 'Пара слов: что предлагаете (необязательно)…')} style={{ ...inputStyle, resize: 'vertical' }} />
        {err && <SaveError text={err} />}
        <div style={{ display: 'flex', gap: 10 }}>
          <button type="button" onClick={onClose} style={{ ...ghostBtn, flex: 1, padding: '11px 12px' }}>{tr('creator.profile.cancel', 'Отмена')}</button>
          <button type="button" disabled={busy} onClick={send} style={{ ...primaryBtn, flex: 2, padding: '11px 12px', textAlign: 'center', opacity: busy ? 0.6 : 1 }}>{busy ? '…' : tr('creator.collab.send', 'Отправить')}</button>
        </div>
      </div>
    </div>
  );
}

// ── Find: search any blogger + smart suggestions ─────────────────────────────
function FindView({ onProposed, relations, onOpenCard }: { onProposed: () => void; relations: Map<string, { status: string; direction: string }>; onOpenCard: (providerId: string, handle: string) => void }) {
  const { t } = useTranslation('provider');
  const tr = (k: string, d: string) => t(k, { defaultValue: d });
  const [q, setQ] = useState('');
  const dq = useDebounced(q, 200);
  const searching = dq.trim().length >= 2;
  const searchQ = useSupabaseQuery<SearchRow[]>(['collab-search', dq.trim()],
    async () => supabase.rpc('creator_search_partners', { p_query: dq.trim(), p_limit: 12 }), { enabled: searching });
  const suggQ = useSupabaseQuery<SuggestRow[]>(['creator-collabs'],
    async () => supabase.rpc('creator_collab_suggestions', { p_limit: 12 }));
  const [compose, setCompose] = useState<{ provider_id: string; handle: string } | null>(null);
  const [proposed, setProposed] = useState<Set<string>>(new Set());
  // Proposing speaks for the creator's account — unverified creators confirm it first.
  const { verified } = useCreator();
  const navigate = useNavigate();
  const propose = (target: { provider_id: string; handle: string }) =>
    verified ? setCompose(target) : navigate('/provider/prices#verify');

  const row = (pid: string, handle: string, followers: number | null, er: number | null, avatar: string | null, onAdamar: boolean, sub?: string) => {
    const done = proposed.has(pid);
    const rel = relations.get(pid);
    const statusLabel =
      rel?.status === 'accepted' ? { txt: tr('creator.collab.inCollab', 'В коллабе'), c: '#37d67a' }
      : rel?.status === 'pending' ? (rel.direction === 'out'
          ? { txt: tr('creator.collab.proposed', 'Отправлено'), c: '#8794a8' }
          : { txt: tr('creator.collab.incomingShort', 'Вам предложили'), c: '#bcd0ff' })
      : done ? { txt: tr('creator.collab.proposed', 'Отправлено'), c: '#8794a8' }
      : null;
    return (
      <div key={pid} style={{ ...card, display: 'flex', alignItems: 'center', gap: 10 }}>
        <div onClick={() => onOpenCard(pid, handle)} style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1, minWidth: 0, cursor: 'pointer' }}>
          <Avatar src={avatar} name={handle} size={38} />
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ color: 'var(--accent-text)', fontWeight: 600, fontSize: 14.5, display: 'flex', alignItems: 'center', gap: 6 }}>
              @{handle}
              <span onClick={(e) => e.stopPropagation()} style={{ display: 'inline-flex' }}><IgLink handle={handle} size={15} /></span>
              {!onAdamar && <span style={{ fontSize: 11, color: 'var(--dim)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 6, padding: '1px 5px' }}>{tr('creator.collab.notOnAdamar', 'не в AdaMar')}</span>}
            </div>
            <div style={{ display: 'flex', gap: 12, marginTop: 3, color: 'var(--muted)', fontSize: 12 }}>
              <span><NamedIcon name="user" size={11} /> {fmtK(followers)}</span>
              {er != null && <span><NamedIcon name="heart" size={11} /> {Number(er).toFixed(1)}%</span>}
            </div>
            {sub && <div style={{ marginTop: 4, fontSize: 11, color: '#8a5cff' }}>{sub}</div>}
          </div>
        </div>
        {statusLabel
          ? <span style={{ fontSize: 12.5, fontWeight: 600, color: statusLabel.c, whiteSpace: 'nowrap' }}>{statusLabel.txt}</span>
          : <button type="button" onClick={() => propose({ provider_id: pid, handle })} style={primaryBtn}>{tr('creator.collab.propose', 'Предложить')}</button>}
      </div>
    );
  };

  const typing = q.trim().length >= 2;   // show the panel as soon as ≥2 chars are typed
  const pending = dq.trim() !== q.trim() || searchQ.isFetching;  // debounce or fetch in flight

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={tr('creator.collab.searchPh', 'Поиск блогера по нику или имени…')} style={inputStyle} />

      {typing ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {pending && !searchQ.data?.length ? <div style={{ color: 'var(--dim)', fontSize: 13, padding: 12 }}>{tr('creator.collab.searching', 'Ищем…')}</div>
            : !searchQ.data?.length ? <div style={{ color: 'var(--dim)', fontSize: 13, padding: 12 }}>{tr('creator.collab.searchEmpty', 'Никого не нашли по этому запросу.')}</div>
            : searchQ.data.map((r) => row(r.provider_id, r.handle, r.followers, r.er, r.avatar, r.on_adamar))}
        </div>
      ) : (
        <>
          <div style={{ color: 'var(--dim)', fontSize: 12, fontWeight: 600, letterSpacing: 0.3, margin: '2px 2px 0' }}>{tr('creator.collab.suggested', 'Рекомендуем для коллаба')}</div>
          <p style={{ color: 'var(--dim)', fontSize: 12, margin: '0 2px 4px' }}>{tr('creator.collab.sub', 'Блогеры вашего уровня из вашей ниши, с которыми вы ещё не сотрудничали.')}</p>
          {suggQ.isLoading ? <div style={{ color: 'var(--dim)', padding: 16, textAlign: 'center' }}>…</div>
            : !suggQ.data?.length ? <div style={{ color: 'var(--dim)', fontSize: 13, padding: 12 }}>{tr('creator.collab.empty', 'Пока нет подходящих кандидатов — укажите нишу в профиле.')}</div>
            : suggQ.data.map((r) => row(r.provider_id, r.handle, r.followers, r.er, r.avatar, true, r.why ? `${tr('creator.collab.shared', 'Общая ниша')}: ${r.why}` : undefined))}
        </>
      )}

      {compose && <ComposeModal target={compose}
        onClose={() => setCompose(null)}
        onSent={() => { setProposed((s) => new Set(s).add(compose.provider_id)); setCompose(null); onProposed(); }} />}
    </div>
  );
}

// ── My collabs: incoming / active / sent ─────────────────────────────────────
function MineView({ rows, onOpenChat, onChanged, onOpenCard }: { rows: CollabRow[]; onOpenChat: (r: CollabRow) => void; onChanged: () => void; onOpenCard: (providerId: string, handle: string) => void }) {
  const { t } = useTranslation('provider');
  const tr = (k: string, d: string) => t(k, { defaultValue: d });
  const [busyId, setBusyId] = useState<string | null>(null);
  const [errId, setErrId] = useState<string | null>(null);

  const act = async (rpc: string, args: Record<string, unknown>, id: string) => {
    setBusyId(id); setErrId(null);
    const { error } = await supabase.rpc(rpc, args);
    setBusyId(null);
    if (error) { setErrId(id); return; }
    onChanged();
  };
  const actErr = (id: string) => errId === id && <SaveError text={tr('creator.collab.actFailed', 'Не получилось. Проверьте интернет и попробуйте ещё раз.')} />;

  const incoming = rows.filter((r) => r.direction === 'in' && r.status === 'pending');
  const active = rows.filter((r) => r.status === 'accepted');
  const sent = rows.filter((r) => r.direction === 'out' && r.status === 'pending');

  if (!incoming.length && !active.length && !sent.length) {
    return <div style={{ color: 'var(--dim)', fontSize: 13, padding: 20, textAlign: 'center' }}>{tr('creator.collab.mineEmpty', 'Пока нет коллабов. Найдите блогера и предложите первым.')}</div>;
  }

  const head = (label: string) => <div style={{ color: 'var(--dim)', fontSize: 12, fontWeight: 600, letterSpacing: 0.3, margin: '8px 2px 2px' }}>{label}</div>;
  const partner = (r: CollabRow) => (
    <div role="button" tabIndex={0} onClick={() => onOpenCard(r.other_provider_id, r.other_handle)}
      onKeyDown={(e) => { if (e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); onOpenCard(r.other_provider_id, r.other_handle); } }}
      style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1, minWidth: 0, cursor: 'pointer' }}>
      <Avatar src={r.other_avatar} name={r.other_handle} size={38} />
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ color: 'var(--accent-text)', fontWeight: 600, fontSize: 14.5, display: 'flex', alignItems: 'center', gap: 6 }}>
          @{r.other_handle}
          <span onClick={(e) => e.stopPropagation()} style={{ display: 'inline-flex' }}><IgLink handle={r.other_handle} size={15} /></span>
        </div>
        <div style={{ color: 'var(--muted)', fontSize: 12, marginTop: 2 }}><NamedIcon name="user" size={11} /> {fmtK(r.other_followers)}</div>
      </div>
    </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {incoming.length > 0 && head(tr('creator.collab.incoming', 'Входящие предложения'))}
      {incoming.map((r) => (
        <div key={r.proposal_id} style={{ ...card }}>
          {partner(r)}
          {r.message && <div style={{ color: '#c2cede', fontSize: 13, margin: '8px 0 4px', lineHeight: 1.4 }}>«{r.message}»</div>}
          <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
            <button type="button" disabled={busyId === r.proposal_id} onClick={() => act('creator_respond_collab', { p_proposal: r.proposal_id, p_accept: true }, r.proposal_id)} style={{ ...primaryBtn, flex: 1, textAlign: 'center', padding: '9px 12px' }}>{tr('creator.collab.accept', 'Принять')}</button>
            <button type="button" disabled={busyId === r.proposal_id} onClick={() => act('creator_respond_collab', { p_proposal: r.proposal_id, p_accept: false }, r.proposal_id)} style={{ ...ghostBtn, flex: 1, textAlign: 'center', padding: '9px 12px' }}>{tr('creator.collab.decline', 'Отклонить')}</button>
          </div>
          {actErr(r.proposal_id)}
        </div>
      ))}

      {active.length > 0 && head(tr('creator.collab.active', 'Активные коллабы'))}
      {active.map((r) => (
        <div key={r.proposal_id} style={{ ...card, display: 'flex', alignItems: 'center', gap: 10 }}>
          {partner(r)}
          <button type="button" onClick={() => onOpenChat(r)} aria-label={tr('creator.collab.openChat', 'Открыть чат')}
            style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3, background: 'transparent', border: 'none', cursor: 'pointer', padding: 4, color: '#8fb0e6' }}>
            {r.unread > 0 && <span style={{ background: '#e74c3c', color: '#fff', borderRadius: 9, fontSize: 11, fontWeight: 700, minWidth: 18, height: 18, lineHeight: '18px', textAlign: 'center', padding: '0 4px' }}>{r.unread > 9 ? '9+' : r.unread}</span>}
            <NamedIcon name="chat" size={18} />
          </button>
        </div>
      ))}

      {sent.length > 0 && head(tr('creator.collab.sent', 'Отправленные'))}
      {sent.map((r) => (
        <div key={r.proposal_id} style={{ ...card, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10 }}>
          {partner(r)}
          <span style={{ color: 'var(--dim)', fontSize: 12 }}>{tr('creator.collab.waiting', 'Ждём ответа')}</span>
          <button type="button" disabled={busyId === r.proposal_id} onClick={() => act('creator_cancel_collab', { p_proposal: r.proposal_id }, r.proposal_id)} style={ghostBtn}>{tr('creator.collab.cancel', 'Отменить')}</button>
          {actErr(r.proposal_id)}
        </div>
      ))}
    </div>
  );
}

// ── Chat thread (polled) ─────────────────────────────────────────────────────
function ChatView({ row, onBack }: { row: CollabRow; onBack: () => void }) {
  const { t } = useTranslation('provider');
  const tr = (k: string, d: string) => t(k, { defaultValue: d });
  const msgsQ = useSupabaseQuery<MsgRow[]>(['collab-msgs', row.proposal_id],
    async () => supabase.rpc('get_my_collab_messages', { p_proposal: row.proposal_id }), { refetchInterval: 4000 });
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [sendErr, setSendErr] = useState(false);
  const endRef = useRef<HTMLDivElement>(null);
  const msgs = msgsQ.data ?? [];
  useEffect(() => { endRef.current?.scrollIntoView({ block: 'end' }); }, [msgs.length]);

  const send = async () => {
    const body = text.trim();
    if (!body) return;
    setBusy(true); setSendErr(false);
    const { error } = await supabase.rpc('creator_send_collab_message', { p_proposal: row.proposal_id, p_body: body });
    setBusy(false);
    if (error) { setSendErr(true); return; }
    setText(''); msgsQ.refetch();
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 140px)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '2px 2px 10px', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
        <button type="button" onClick={onBack} style={{ ...ghostBtn, padding: '5px 10px' }}>←</button>
        <Avatar src={row.other_avatar} name={row.other_handle} size={30} />
        <span style={{ color: '#eaf1fb', fontWeight: 600, fontSize: 15 }}>@{row.other_handle}</span>
      </div>

      <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 6, padding: '12px 2px' }}>
        {!msgs.length && <div style={{ color: 'var(--dim)', fontSize: 13, textAlign: 'center', margin: 'auto' }}>{tr('creator.collab.chatEmpty', 'Напишите первым — договоритесь о деталях коллаба.')}</div>}
        {msgs.map((m) => (
          <div key={m.id} style={{ maxWidth: '78%', alignSelf: m.mine ? 'flex-end' : 'flex-start',
            background: m.mine ? 'rgba(74,158,255,0.22)' : 'rgba(255,255,255,0.06)',
            border: '1px solid ' + (m.mine ? 'rgba(74,158,255,0.35)' : 'rgba(255,255,255,0.08)'),
            color: '#eaf1fb', borderRadius: 12, padding: '8px 11px', fontSize: 14, lineHeight: 1.4, wordBreak: 'break-word' }}>
            {m.body}
          </div>
        ))}
        <div ref={endRef} />
      </div>

      {sendErr && <SaveError text={tr('creator.collab.sendFailed', 'Сообщение не отправилось. Текст сохранён — попробуйте ещё раз.')} />}
      <div style={{ display: 'flex', gap: 8, paddingTop: 8, borderTop: '1px solid rgba(255,255,255,0.06)' }}>
        <input value={text} aria-label={tr('creator.collab.msgInput', 'Сообщение…')} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
          placeholder={tr('creator.collab.msgInput', 'Сообщение…')} style={{ ...inputStyle, flex: 1 }} />
        <button type="button" disabled={busy || !text.trim()} onClick={send} aria-label={tr('creator.collab.send', 'Отправить')} style={{ ...primaryBtn, padding: '0 16px', minWidth: 44, minHeight: 44, opacity: busy || !text.trim() ? 0.5 : 1 }}>
          <NamedIcon name="send" size={16} />
        </button>
      </div>
    </div>
  );
}

// ── Screen ───────────────────────────────────────────────────────────────────
export default function CreatorCollab() {
  const { t } = useTranslation('provider');
  const tr = (k: string, d: string) => t(k, { defaultValue: d });
  const invalidate = useInvalidateSupabase();
  const [tab, setTab] = useState<'find' | 'mine'>('find');
  const [chat, setChat] = useState<CollabRow | null>(null);
  const [card, setCard] = useState<{ providerId: string; handle: string } | null>(null);
  const openCard = (providerId: string, handle: string) => setCard({ providerId, handle });

  const collabsQ = useSupabaseQuery<CollabRow[]>(['my-collabs'],
    async () => supabase.rpc('get_my_collabs'), { refetchInterval: tab === 'mine' && !chat ? 8000 : false });
  const rows = collabsQ.data ?? [];
  const badge = useMemo(() =>
    rows.filter((r) => r.direction === 'in' && r.status === 'pending').length
    + rows.reduce((a, r) => a + (r.status === 'accepted' ? r.unread : 0), 0), [rows]);
  // provider_id → existing relationship, so search/suggestions show a status instead of "Предложить".
  const relations = useMemo(() => {
    const m = new Map<string, { status: string; direction: string }>();
    for (const r of rows) if (r.status === 'pending' || r.status === 'accepted') m.set(r.other_provider_id, { status: r.status, direction: r.direction });
    return m;
  }, [rows]);

  if (chat) {
    const fresh = rows.find((r) => r.proposal_id === chat.proposal_id) ?? chat;
    return (
      <div className="cr-page">
        <ChatView row={fresh} onBack={() => { setChat(null); collabsQ.refetch(); }} />
      </div>
    );
  }

  const pill = (v: 'find' | 'mine', label: string, n?: number) => (
    <button type="button" onClick={() => setTab(v)} style={{ flex: 1, position: 'relative', padding: '8px 10px', borderRadius: 10, fontFamily: 'inherit', fontSize: 13.5, fontWeight: 600, cursor: 'pointer',
      border: '1px solid ' + (tab === v ? 'rgba(74,158,255,0.4)' : 'rgba(255,255,255,0.1)'),
      background: tab === v ? 'rgba(74,158,255,0.16)' : 'transparent', color: tab === v ? 'var(--accent-text)' : 'var(--dim)' }}>
      {label}
      {!!n && <span style={{ marginLeft: 6, background: '#e74c3c', color: '#fff', borderRadius: 9, fontSize: 11, fontWeight: 700, padding: '1px 6px' }}>{n > 9 ? '9+' : n}</span>}
    </button>
  );

  return (
    <div className="cr-page">
      <h2 style={{ color: 'var(--text)', fontSize: 18, margin: '4px 0 10px' }}>{tr('creator.collab.title', 'Коллабы')}</h2>
      <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
        {pill('find', tr('creator.collab.tabFind', 'Найти'))}
        {pill('mine', tr('creator.collab.tabMine', 'Мои коллабы'), badge)}
      </div>
      {tab === 'find'
        ? <FindView onProposed={() => invalidate(['my-collabs'])} relations={relations} onOpenCard={openCard} />
        : <MineView rows={rows} onOpenChat={setChat} onChanged={() => collabsQ.refetch()} onOpenCard={openCard} />}

      {card && <CreatorCardModal providerId={card.providerId} handle={card.handle} onClose={() => setCard(null)} />}
    </div>
  );
}
